import React, { useEffect, useState, useRef, useCallback } from "react";
import MainLayout from "../../Layout/mainLayout";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUsersList } from "../../Redux/Actions/usersActions";
import { viewAllCity } from "../../Redux/Actions/settingsActions";
import { TimeslotList } from "../../Redux/Actions/agentActions";
import { listAddress, addAddress, changeLeadStatus, removeAddress } from "../../Redux/Actions/leadActions";
import { agentProfitRangeList } from "../../Redux/Actions/agentProfitRangeActions";
import { GetVrThreeSixtyProjects } from "../../Redux/Actions/homeVrActions";
import { createAppointment } from "../../Redux/Actions/leadActions";
import { GoogleMap, useLoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import toast from "react-hot-toast";
import "./createAppointment.css";

const libraries = ["places"];

const mapContainerStyle = {
  width: "100%",
  height: "360px",
  borderRadius: "10px",
};

const defaultCenter = { lat: 28.6139, lng: 77.209 };

const AddAppointment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const { citiesList } = useSelector((state) => state.city || {});
  const { timeslots } = useSelector((state) => state.agents || {});
  const { agentProfitRanges } = useSelector((state) => state.agentProfitRange || {});
  const vrState = useSelector((s) => s.vrHome || {});
  const threeSixtyProjects = vrState.threeSixtyProjects || [];

  const [addresses, setAddresses] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({
    house_flat_floorno: "",
    appartment_road_area: "",
    direction_to_reach: "",
    address: "",
    save_as: "home",
    lat: 0,
    lng: 0,
  });

  const [removingAddressId, setRemovingAddressId] = useState(null);
  const [addressToDelete, setAddressToDelete] = useState(null);

  const handleAskRemoveAddress = (e, addressId) => {
    e.stopPropagation();
    setAddressToDelete(addressId);
  };

  const handleConfirmRemoveAddress = async () => {
    const addressId = addressToDelete;
    if (!addressId) return;

    try {
      setRemovingAddressId(addressId);
      const res = await dispatch(removeAddress({ address_id: addressId }));
      if (res?.statusCode === 200) {
        setAddresses((prev) => prev.filter((a) => a._id !== addressId));
        setFormData((prev) => ({
          ...prev,
          address_id: prev.address_id === addressId ? "" : prev.address_id,
          ...(prev.address_id === addressId && { lat: 0, lng: 0 }),
        }));
      }
    } finally {
      setRemovingAddressId(null);
      setAddressToDelete(null);
    }
  };

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries,
  });

  // ── Form State ──
  const [formData, setFormData] = useState({
    user_id: "",
    cities: [],
    lat: 0,
    lng: 0,
    time_slot: "",
    profit_range: "",
    projects: [],
    address_id: "",
    appointment_date: "",
    business_type: "fresh",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // ── User search state ──
  const [phoneSearch, setPhoneSearch] = useState("");
  const [userSearching, setUserSearching] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const searchTimeout = useRef(null);

  // ── Map state ──
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);

  // ── City dropdown state ──
  const [citySearch, setCitySearch] = useState("");
  const [cityOpen, setCityOpen] = useState(false);
  const cityDropRef = useRef(null);
  const hasFetchedCities = useRef(false);

  // ── Fetch on mount ──
  useEffect(() => {
    if (!citiesList?.data?.length && !hasFetchedCities.current) {
      dispatch(viewAllCity());
      hasFetchedCities.current = true;
    }
    dispatch(TimeslotList({ page: 1, limit: 1000, is_deleted: false }));
  }, [dispatch]);

  useEffect(() => {
    if (!formData.cities.length) return;
    dispatch(GetVrThreeSixtyProjects(formData.cities[0]));
    setFormData((prev) => ({ ...prev, projects: [] }));
  }, [formData.cities]);

  useEffect(() => {
    if (!citiesList?.data?.length && !hasFetchedCities.current) {
      dispatch(viewAllCity());
      hasFetchedCities.current = true;
    }
    dispatch(TimeslotList({ page: 1, limit: 1000, is_deleted: false }));
    dispatch(agentProfitRangeList({ page: 1, limit: 100 }));
  }, [dispatch]);

  // ── City dropdown outside click ──
  useEffect(() => {
    if (!cityOpen) return;
    const handler = (e) => {
      if (cityDropRef.current && !cityDropRef.current.contains(e.target)) {
        setCityOpen(false);
        setCitySearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [cityOpen]);

  useEffect(() => {
    const contact = location.state?.contact_number;
    if (contact) {
      setPhoneSearch(contact);
    }
  }, [location.state]);

  // ── User phone search (debounced) ──
  useEffect(() => {
    if (!phoneSearch.trim()) {
      setFoundUser(null);
      setFormData((prev) => ({ ...prev, user_id: "" }));
      return;
    }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        setUserSearching(true);
        const res = await dispatch(
          getUsersList({ page: 1, limit: 5, filter: phoneSearch, signup_from: "app", is_deleted: false })
        );
        const users = res?.data || [];
        if (users.length > 0) {
          const user = users[0];
          setFoundUser(user);
          setFormData((prev) => ({ ...prev, user_id: user._id }));
        } else {
          setFoundUser(null);
          setFormData((prev) => ({ ...prev, user_id: "" }));
        }
      } catch {
        setFoundUser(null);
      } finally {
        setUserSearching(false);
      }
    }, 600);
    return () => clearTimeout(searchTimeout.current);
  }, [phoneSearch, dispatch]);

  useEffect(() => {
    if (!formData.user_id) {
      setAddresses([]);
      return;
    }
    const fetchAddresses = async () => {
      const res = await dispatch(listAddress({ user_id: formData.user_id }));
      if (res?.statusCode === 200) {
        const list = res.data.data || [];
        list.forEach(a => console.log(a._id, a.location?.coordinates));
        setAddresses(list);
      }
    };
    fetchAddresses();
  }, [formData.user_id]);

  // ── Map handlers ──
  const onMapLoad = useCallback((map) => { mapRef.current = map; }, []);

  // const onMapClick = useCallback((event) => {
  //   const lat = event.latLng.lat();
  //   const lng = event.latLng.lng();
  //   setMarkerPosition({ lat, lng });
  //   setMapCenter({ lat, lng });
  //   setFormData((prev) => ({ ...prev, lat, lng }));
  //   setErrors((prev) => ({ ...prev, location: "" }));
  // }, []);

  const onAutocompleteLoad = (ac) => { autocompleteRef.current = ac; };

  const handleAddAddress = async () => {
    if (!addressForm.house_flat_floorno || !addressForm.save_as) {
      return toast.error("House/Flat No and Save As are required");
    }

    try {
      setAddressLoading(true);
      const payload = {
        user_id: formData.user_id,
        ...addressForm,
      };
      const res = await dispatch(addAddress(payload));
      if (res?.statusCode === 200) {
        const listRes = await dispatch(listAddress({ user_id: formData.user_id }));
        if (listRes?.statusCode === 200) {
          const list = listRes.data.data || [];
          setAddresses(list);

          const newAddr = res.data;
          const matched = list.find((a) => a._id === newAddr._id) || newAddr;

          setFormData((prev) => ({
            ...prev,
            address_id: matched._id,
            lat: matched.location?.coordinates?.[1] || addressForm.lat,
            lng: matched.location?.coordinates?.[0] || addressForm.lng,
          }));
        }
        setShowAddressForm(false);
        setAddressForm({
          house_flat_floorno: "",
          appartment_road_area: "",
          direction_to_reach: "",
          address: "",
          save_as: "home",
          lat: 0,
          lng: 0,
        });
      }
    } catch {
      toast.error("Failed to add address");
    } finally {
      setAddressLoading(false);
    }
  };

  // const onPlaceChanged = () => {
  //   if (!autocompleteRef.current) return;
  //   const place = autocompleteRef.current.getPlace();
  //   if (place.geometry) {
  //     const lat = place.geometry.location.lat();
  //     const lng = place.geometry.location.lng();
  //     setMarkerPosition({ lat, lng });
  //     setMapCenter({ lat, lng });
  //     setFormData((prev) => ({ ...prev, lat, lng }));
  //     setErrors((prev) => ({ ...prev, location: "" }));
  //   }
  // };

  // ── City helpers ──
  const filteredCities = (citiesList?.data || []).filter((c) =>
    c.city_name.toLowerCase().includes(citySearch.toLowerCase())
  );

  const selectedCityName = formData.cities.length
    ? (citiesList?.data || []).find((c) => c._id === formData.cities[0])?.city_name || ""
    : "";

  const handleCitySelect = (id) => {
    setFormData((prev) => ({ ...prev, cities: [id] }));
    setErrors((prev) => ({ ...prev, cities: "" }));
    setCityOpen(false);
    setCitySearch("");
  };

  const handleCityClear = (e) => {
    e.stopPropagation();
    setFormData((prev) => ({ ...prev, cities: [] }));
  };

  // ── Project toggle ──
  const handleProjectToggle = (id) => {
    setFormData((prev) => {
      const already = prev.projects.includes(id);
      return {
        ...prev,
        projects: already ? prev.projects.filter((p) => p !== id) : [...prev.projects, id],
      };
    });
    setErrors((prev) => ({ ...prev, projects: "" }));
  };

  const getTomorrowDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  };

  // ── Validation ──
  const validate = () => {
    const e = {};
    if (!formData.user_id) e.user_id = "Please enter a valid phone number to find a user";
    if (!formData.cities.length) e.cities = "Please select a city";
    if (!formData.time_slot) e.time_slot = "Please select a time slot";
    if (!formData.appointment_date) e.appointment_date = "Please select an appointment date";
    // if (formData.lat === 0 && formData.lng === 0) e.location = "Please pick a location on the map";
    if (!formData.projects.length) e.projects = "Please select at least one project";
    setErrors(e);
    console.log("validation errors:", e);
    return Object.keys(e).length === 0;
  };

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return toast.error("Please fix the errors before submitting");

    // derive coords from selected address directly
    const selectedAddr = addresses.find(a => a._id === formData.address_id);
    const coords = selectedAddr?.location?.coordinates;
    const lat = coords?.[1] || formData.lat;
    const lng = coords?.[0] || formData.lng;

    try {
      setLoading(true);
      const payload = {
        user_id: formData.user_id,
        cities: formData.cities,
        lat,
        lng,
        time_slot: formData.time_slot,
        ...(formData.profit_range && { profit_range: formData.profit_range }),
        projects: formData.projects,
        ...(formData.address_id && { address_id: formData.address_id }),
        appointment_date: new Date(formData.appointment_date).toISOString(),
        business_type: "fresh",
      };
      console.log("payload:", JSON.stringify(payload, null, 2));
      const res = await dispatch(createAppointment(payload));
      if (res?.statusCode === 200) {
        if (id) {
          try {
            await dispatch(changeLeadStatus({ lead_id: id, status: "scheduled" }));
          } catch {
          }
        }
        toast.success("Appointment created successfully!");
        navigate(-1);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to create appointment");
    } finally {
      setLoading(false);
    }
  };

  // const hasLocation = formData.lat !== 0 || formData.lng !== 0;

  const uniqueSlots = Array.isArray(timeslots)
    ? [...new Set(timeslots.map((t) => t.time).filter(Boolean))]
    : [];

  return (
    <MainLayout>
      <div className="aap-wrapper">

        {/* Header */}
        <div className="aap-header">
          <div className="aap-header-left">
            <div className="aap-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div>
              <h1 className="aap-title">Add Appointment</h1>
              <p className="aap-subtitle">Schedule a new site visit appointment</p>
            </div>
          </div>
          <button onClick={() => navigate(-1)} className="aap-view-btn" type="button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
        </div>

        <form onSubmit={handleSubmit} className="aap-form" noValidate>

          {/* ── Section 01: User ── */}
          <div className="aap-section">
            <div className="aap-section-header">
              <span className="aap-section-badge">01</span>
              <h2 className="aap-section-title">User</h2>
            </div>

            {userSearching ? (
              <div className="aap-user-loading">
                <span className="aap-search-spinner" />
                <span className="aap-hint">Looking up user...</span>
              </div>
            ) : foundUser ? (
              <div className="aap-user-card">
                <div className="aap-user-avatar">
                  {foundUser.name?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <div className="aap-user-info">
                  <p className="aap-user-name">{foundUser.name}</p>
                  <p className="aap-user-meta">{foundUser.country_code} {foundUser.contact_number}</p>
                  {foundUser.email && <p className="aap-user-meta">{foundUser.email}</p>}
                </div>
                <div className="aap-user-check">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Matched
                </div>
              </div>
            ) : (
              <div className="aap-assoc-empty">
                <p>No user found for this lead</p>
              </div>
            )}

            {errors.user_id && <span className="aap-error-msg aap-mt-8">{errors.user_id}</span>}
          </div>

          {/* ── Section 03: Schedule ── */}
          <div className="aap-section">
            <div className="aap-section-header">
              <span className="aap-section-badge">02</span>
              <h2 className="aap-section-title">Schedule</h2>
            </div>

            <div className="aap-grid aap-grid-2">
              <div className="aap-field">
                <label className="aap-label">Appointment Date <span className="aap-req">*</span></label>
                <div className="aap-date-wrap">
                  <svg className="aap-date-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <input
                    type="date"
                    className={`aap-input aap-input--date ${errors.appointment_date ? "aap-input--error" : ""}`}
                    value={formData.appointment_date ? formData.appointment_date.substring(0, 10) : ""}
                    min={getTomorrowDate()}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, appointment_date: e.target.value }));
                      setErrors((prev) => ({ ...prev, appointment_date: "" }));
                    }}
                    disabled={loading}
                  />
                </div>
                {errors.appointment_date && <span className="aap-error-msg">{errors.appointment_date}</span>}
              </div>

              <div className="aap-field">
                <label className="aap-label">Time Slot <span className="aap-req">*</span></label>
                <div className="aap-select-wrap">
                  <svg className="aap-select-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <select
                    className={`aap-select ${errors.time_slot ? "aap-input--error" : ""}`}
                    value={formData.time_slot}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, time_slot: e.target.value }));
                      setErrors((prev) => ({ ...prev, time_slot: "" }));
                    }}
                    disabled={loading}
                  >
                    <option value="">Select a time slot...</option>
                    {uniqueSlots.map((slot) => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                  <svg className="aap-select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {errors.time_slot && <span className="aap-error-msg">{errors.time_slot}</span>}
              </div>
            </div>
          </div>

          <div className="aap-section">
            <div className="aap-section-header">
              <span className="aap-section-badge">03</span>
              <h2 className="aap-section-title">Address & Location</h2>
            </div>

            {!formData.user_id ? (
              <div className="aap-assoc-empty"><p>User must be matched first</p></div>
            ) : (
              <>
                {/* Existing addresses */}
                {addresses.length > 0 && (
                  <div className="aap-address-list">
                    {addresses.map((addr) => (
                      <div
                        key={addr._id}
                        className={`aap-address-card ${formData.address_id === addr._id ? "aap-address-card--selected" : ""}`}
                        onClick={() => {
                          const coords = addr.location?.coordinates;
                          const lat = coords?.[1] || 0;
                          const lng = coords?.[0] || 0;
                          setFormData((prev) => ({
                            ...prev,
                            address_id: addr._id,
                            lat,
                            lng,
                          }));
                        }}
                      >
                        <div className="aap-address-card-left">
                          <span className="aap-address-tag">{addr.save_as}</span>
                          <p className="aap-address-text">{addr.house_flat_floorno}{addr.appartment_road_area ? `, ${addr.appartment_road_area}` : ""}</p>
                          {addr.address && <p className="aap-address-sub">{addr.address}</p>}
                          {addr.lat && addr.lng && (
                            <p className="aap-address-sub">📍 {addr.lat?.toFixed(5)}, {addr.lng?.toFixed(5)}</p>
                          )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {formData.address_id === addr._id && (
                            <div className="aap-address-check">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </div>
                          )}
                          <button
                            type="button"
                            className="aap-address-remove-btn"
                            onClick={(e) => handleAskRemoveAddress(e, addr._id)}
                            disabled={removingAddressId === addr._id}
                            title="Remove address"
                          >
                            {removingAddressId === addr._id ? (
                              <span className="aap-spinner" />
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                <path d="M10 11v6M14 11v6" />
                                <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new address toggle */}
                {!showAddressForm ? (
                  <button type="button" className="aap-add-address-btn" onClick={() => setShowAddressForm(true)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add New Address
                  </button>
                ) : (
                  <div className="aap-address-form">

                    {/* ── Map + Search inside form ── */}
                    {loadError && (
                      <div className="aap-map-placeholder">
                        <div className="aap-map-placeholder-icon">🗺️</div>
                        <p className="aap-map-placeholder-title">Map unavailable</p>
                        <p className="aap-map-placeholder-desc">Check your Google Maps API key</p>
                      </div>
                    )}
                    {!isLoaded && !loadError && (
                      <div className="aap-map-placeholder">
                        <div className="aap-map-loading-spinner" />
                        <p className="aap-map-placeholder-title">Loading map...</p>
                      </div>
                    )}
                    {isLoaded && !loadError && (
                      <div className="aap-map-wrap" style={{ marginBottom: "20px" }}>

                        {/* Search + Lat/Lng row */}
                        <div style={{ display: "flex", gap: "8px", marginBottom: "10px", alignItems: "center" }}>
                          <Autocomplete
                            onLoad={onAutocompleteLoad}
                            onPlaceChanged={() => {
                              if (!autocompleteRef.current) return;
                              const place = autocompleteRef.current.getPlace();
                              if (place.geometry) {
                                const lat = place.geometry.location.lat();
                                const lng = place.geometry.location.lng();
                                setMarkerPosition({ lat, lng });
                                setMapCenter({ lat, lng });
                                setFormData((prev) => ({ ...prev, lat, lng }));
                                setAddressForm((prev) => ({ ...prev, lat, lng }));
                                // auto-fill address field from place name
                                setAddressForm((prev) => ({
                                  ...prev,
                                  lat,
                                  lng,
                                  address: place.formatted_address || prev.address,
                                }));
                                setErrors((prev) => ({ ...prev, location: "" }));
                              }
                            }}
                            style={{ flex: 1 }}
                          >
                            <input
                              type="text"
                              placeholder="Search area or address..."
                              className="aap-input aap-map-search"
                              style={{ margin: 0, width: "450%" }}
                            />
                          </Autocomplete>

                          <input
                            type="number"
                            placeholder="Latitude"
                            className="aap-input"
                            style={{ width: "130px", flexShrink: 0, marginLeft: "57%" }}
                            value={addressForm.lat || ""}
                            onChange={(e) => {
                              const lat = parseFloat(e.target.value);
                              setAddressForm((prev) => ({ ...prev, lat: isNaN(lat) ? 0 : lat }));
                            }}
                            onBlur={() => {
                              if (addressForm.lat && addressForm.lng) {
                                const pos = { lat: addressForm.lat, lng: addressForm.lng };
                                setMarkerPosition(pos);
                                setMapCenter(pos);
                                setFormData((prev) => ({ ...prev, lat: addressForm.lat, lng: addressForm.lng }));
                                setErrors((prev) => ({ ...prev, location: "" }));
                              }
                            }}
                          />

                          <input
                            type="number"
                            placeholder="Longitude"
                            className="aap-input"
                            style={{ width: "130px", flexShrink: 0 }}
                            value={addressForm.lng || ""}
                            onChange={(e) => {
                              const lng = parseFloat(e.target.value);
                              setAddressForm((prev) => ({ ...prev, lng: isNaN(lng) ? 0 : lng }));
                            }}
                            onBlur={() => {
                              if (addressForm.lat && addressForm.lng) {
                                const pos = { lat: addressForm.lat, lng: addressForm.lng };
                                setMarkerPosition(pos);
                                setMapCenter(pos);
                                setFormData((prev) => ({ ...prev, lat: addressForm.lat, lng: addressForm.lng }));
                                setErrors((prev) => ({ ...prev, location: "" }));
                              }
                            }}
                          />
                        </div>

                        {/* Map */}
                        <GoogleMap
                          mapContainerStyle={mapContainerStyle}
                          center={mapCenter}
                          zoom={13}
                          onClick={(event) => {
                            const lat = event.latLng.lat();
                            const lng = event.latLng.lng();
                            setMarkerPosition({ lat, lng });
                            setMapCenter({ lat, lng });
                            setFormData((prev) => ({ ...prev, lat, lng }));
                            setAddressForm((prev) => ({ ...prev, lat, lng }));
                            setErrors((prev) => ({ ...prev, location: "" }));
                          }}
                          onLoad={onMapLoad}
                        >
                          {markerPosition && <Marker position={markerPosition} />}
                        </GoogleMap>

                        <p className="aap-map-hint">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                          </svg>
                          Click on the map, search by name, or enter lat/lng manually
                        </p>

                        {errors.location && <span className="aap-error-msg aap-mt-8">{errors.location}</span>}
                      </div>
                    )}

                    {/* ── Address Fields ── */}
                    <div className="aap-grid aap-grid-2">
                      <div className="aap-field">
                        <label className="aap-label">House / Flat / Floor No <span className="aap-req">*</span></label>
                        <input className="aap-input" type="text" placeholder="e.g. Flat 4B, 2nd Floor"
                          value={addressForm.house_flat_floorno}
                          onChange={(e) => setAddressForm((prev) => ({ ...prev, house_flat_floorno: e.target.value }))}
                        />
                      </div>

                      <div className="aap-field">
                        <label className="aap-label">Apartment / Road / Area</label>
                        <input className="aap-input" type="text" placeholder="e.g. Sector 18, Noida"
                          value={addressForm.appartment_road_area}
                          onChange={(e) => setAddressForm((prev) => ({ ...prev, appartment_road_area: e.target.value }))}
                        />
                      </div>

                      <div className="aap-field">
                        <label className="aap-label">Direction to Reach</label>
                        <input className="aap-input" type="text" placeholder="e.g. Near metro station"
                          value={addressForm.direction_to_reach}
                          onChange={(e) => setAddressForm((prev) => ({ ...prev, direction_to_reach: e.target.value }))}
                        />
                      </div>

                      <div className="aap-field">
                        <label className="aap-label">Save As <span className="aap-req">*</span></label>
                        <div className="aap-select-wrap">
                          <select className="aap-select" style={{ paddingLeft: "14px" }}
                            value={addressForm.save_as}
                            onChange={(e) => setAddressForm((prev) => ({ ...prev, save_as: e.target.value }))}
                          >
                            <option value="home">Home</option>
                            <option value="work">Work</option>
                            <option value="other">Other</option>
                          </select>
                          <svg className="aap-select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </div>

                      <div className="aap-field aap-field--full">
                        <label className="aap-label">Full Address</label>
                        <input className="aap-input" type="text" placeholder="Full address string"
                          value={addressForm.address}
                          onChange={(e) => setAddressForm((prev) => ({ ...prev, address: e.target.value }))}
                        />
                      </div>
                    </div>

                    {/* ── Actions ── */}
                    <div className="aap-address-form-actions">
                      <button type="button" className="aap-btn aap-btn--cancel" onClick={() => {
                        setShowAddressForm(false);
                        setMarkerPosition(null);
                        setAddressForm({
                          house_flat_floorno: "",
                          appartment_road_area: "",
                          direction_to_reach: "",
                          address: "",
                          save_as: "home",
                          lat: 0,
                          lng: 0,
                        });
                      }}>
                        Cancel
                      </button>
                      <button type="button" className="aap-btn aap-btn--submit" onClick={handleAddAddress} disabled={addressLoading}>
                        {addressLoading ? <><span className="aap-spinner" />Saving...</> : "Save Address"}
                      </button>
                    </div>

                  </div>
                )}
              </>
            )}
          </div>

          <div className="aap-section">
            <div className="aap-section-header">
              <span className="aap-section-badge">04</span>
              <h2 className="aap-section-title">City</h2>
            </div>

            <div className="aap-field">
              <label className="aap-label">Select City <span className="aap-req">*</span></label>
              <div className="aap-city-dropdown" ref={cityDropRef}>
                <button
                  type="button"
                  className={`aap-city-trigger ${errors.cities ? "aap-input--error" : ""}`}
                  onClick={() => setCityOpen(!cityOpen)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span className={selectedCityName ? "aap-city-selected" : "aap-city-placeholder"}>
                    {selectedCityName || "Choose a city..."}
                  </span>
                  {formData.cities.length > 0 && (
                    <span className="aap-city-clear" onClick={handleCityClear}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </span>
                  )}
                  <svg className={`aap-chevron ${cityOpen ? "aap-chevron--open" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {cityOpen && (
                  <div className="aap-city-popup">
                    <div className="aap-city-search-wrap">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <input
                        className="aap-city-search"
                        type="text"
                        placeholder="Search cities..."
                        value={citySearch}
                        onChange={(e) => setCitySearch(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="aap-city-list">
                      {filteredCities.length > 0 ? filteredCities.map((city) => (
                        <button
                          key={city._id}
                          type="button"
                          className={`aap-city-option ${formData.cities[0] === city._id ? "aap-city-option--selected" : ""}`}
                          onClick={() => handleCitySelect(city._id)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                          {city.city_name}
                          {formData.cities[0] === city._id && (
                            <svg className="aap-city-tick" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </button>
                      )) : (
                        <div className="aap-city-empty">No cities found</div>
                      )}
                    </div>
                    <div className="aap-city-footer">{filteredCities.length} cities available</div>
                  </div>
                )}
              </div>
              {errors.cities && <span className="aap-error-msg">{errors.cities}</span>}
            </div>
          </div>

          {/* ── Section 05: Projects ── */}
          <div className="aap-section">
            <div className="aap-section-header">
              <span className="aap-section-badge">05</span>
              <h2 className="aap-section-title">Projects</h2>
            </div>

            {errors.projects && <span className="aap-error-msg aap-mb-12">{errors.projects}</span>}

            <div className="aap-projects-grid">
              {threeSixtyProjects.length > 0 ? threeSixtyProjects.map((p) => {
                const selected = formData.projects.includes(p._id);
                return (
                  <button
                    key={p._id}
                    type="button"
                    className={`aap-project-card ${selected ? "aap-project-card--selected" : ""}`}
                    onClick={() => handleProjectToggle(p._id)}
                    disabled={loading}
                  >
                    <div className="aap-project-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                      </svg>
                    </div>
                    <span className="aap-project-name">{p.project_name || p.name || "—"}</span>
                    {selected && (
                      <span className="aap-project-check">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                    )}
                  </button>
                );
              }) : (
                <div className="aap-projects-empty">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  </svg>
                  <p>No projects available</p>
                </div>
              )}
            </div>

            {formData.projects.length > 0 && (
              <div className="aap-selected-projects">
                <span className="aap-selected-label">{formData.projects.length} project{formData.projects.length > 1 ? "s" : ""} selected</span>
              </div>
            )}
          </div>

          {/* ── Section 06: Additional Info ── */}
          <div className="aap-section">
            <div className="aap-section-header">
              <span className="aap-section-badge">06</span>
              <h2 className="aap-section-title">Additional Info</h2>
            </div>
            <div className="aap-grid aap-grid-2">
              <div className="aap-field">
                <label className="aap-label">Profit Range</label>
                <div className="aap-select-wrap">
                  <svg className="aap-select-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23" />
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  </svg>
                  <select
                    className="aap-select"
                    value={formData.profit_range}
                    onChange={(e) => setFormData((prev) => ({ ...prev, profit_range: e.target.value }))}
                    disabled={loading}
                  >
                    <option value="">Select profit range...</option>
                    {Array.isArray(agentProfitRanges) && agentProfitRanges.map((r) => (
                      <option key={r._id} value={r._id}>
                        {r.category} — ₹{(r.min_price / 100000).toFixed(0)}L to ₹{(r.max_price / 100000).toFixed(0)}L ({r.agent_profit_range}%)
                      </option>
                    ))}
                  </select>
                  <svg className="aap-select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Business type — static display */}
            <div className="aap-business-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2" />
                <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
              </svg>
              Business Type: <strong>Fresh</strong>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="aap-footer">
            <button type="button" className="aap-btn aap-btn--cancel" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="aap-btn aap-btn--submit" disabled={loading}>
              {loading ? (
                <><span className="aap-spinner" />Saving...</>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Book Appointment
                </>
              )}
            </button>
          </div>

        </form>
      </div>
      {addressToDelete && (
        <div className="aap-confirm-overlay" onClick={() => setAddressToDelete(null)}>
          <div className="aap-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="aap-confirm-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <p className="aap-confirm-title">Remove this address?</p>
            <p className="aap-confirm-desc">This action cannot be undone.</p>
            <div className="aap-confirm-actions">
              <button
                type="button"
                className="aap-btn aap-btn--cancel"
                onClick={() => setAddressToDelete(null)}
                disabled={removingAddressId === addressToDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="aap-btn aap-btn--danger"
                onClick={handleConfirmRemoveAddress}
                disabled={removingAddressId === addressToDelete}
              >
                {removingAddressId === addressToDelete ? <><span className="aap-spinner" />Removing...</> : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout >
  );
};

export default AddAppointment;